/**
 * EbayTopBarStatus - Top bar eBay connection indicator
 *
 * Compact status dot + label for the main navigation bar.
 * Connected: shows dropdown with health details + refresh.
 * Disconnected: one-click OAuth popup to reconnect.
 */

import React, { useState, useRef, useEffect } from 'react'
import { RefreshCw, Link2, Unlink, ChevronDown } from 'lucide-react'
import { trpc } from '@/utils/trpc'
import { apiClient } from '@/lib/api-client'
import toast from 'react-hot-toast'

export const EbayTopBarStatus: React.FC = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Token health query (polls every 60s)
  const { data: refreshStatus, isLoading, refetch } = trpc.ebayAuth.getRefreshStatus.useQuery(undefined, {
    refetchInterval: 60000,
  })

  // Force refresh mutation
  const forceRefresh = trpc.ebayAuth.forceRefresh.useMutation({
    onSuccess: () => {
      toast.success('eBay token refreshed')
      refetch()
    },
    onError: (err) => {
      toast.error(`Refresh failed: ${err.message}`)
    },
  })

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    if (dropdownOpen) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [dropdownOpen])

  const isConnected = refreshStatus && refreshStatus.status !== 'inactive' && refreshStatus.status !== 'expired'
  const hasCredentials = refreshStatus && refreshStatus.statistics.activeCredentials > 0

  const getStatusColor = () => {
    if (isLoading) return 'var(--theme-text-tertiary)'
    if (!hasCredentials) return 'var(--theme-error, #ff0000)'
    switch (refreshStatus?.status) {
      case 'healthy': return 'var(--theme-success, #6BCF7F)'
      case 'warning': return 'var(--theme-warning, #FFD93D)'
      case 'critical': return 'var(--theme-error, #FF6B35)'
      case 'expired': return 'var(--theme-error, #ff0000)'
      default: return 'var(--theme-text-tertiary)'
    }
  }

  const getStatusLabel = () => {
    if (!refreshStatus) return null
    switch (refreshStatus.status) {
      case 'healthy': return 'Healthy'
      case 'warning': return 'Expiring Soon'
      case 'critical': return 'Critical'
      case 'expired': return 'Expired'
      case 'inactive': return 'Disconnected'
      default: return null
    }
  }

  // OAuth popup connect flow
  const handleConnect = async () => {
    setConnecting(true)
    try {
      const response = await apiClient.get('/api/ebay/auth/connect', {
        params: { environment: 'production', returnUrl: window.location.href },
      })
      const { authUrl } = response.data
      if (!authUrl) throw new Error('No auth URL received')

      const w = 600, h = 700
      const left = window.screen.width / 2 - w / 2
      const top = window.screen.height / 2 - h / 2
      const popup = window.open(authUrl, 'eBay OAuth', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`)

      if (!popup) {
        toast.error('Popup blocked — please allow popups for this site')
        setConnecting(false)
        return
      }

      const poll = setInterval(() => {
        if (popup.closed) {
          clearInterval(poll)
          setConnecting(false)
          setTimeout(() => refetch(), 500)
        }
      }, 500)

      setTimeout(() => {
        if (!popup.closed) {
          popup.close()
          clearInterval(poll)
          setConnecting(false)
          toast.error('OAuth timed out')
        }
      }, 5 * 60 * 1000)
    } catch {
      toast.error('Failed to start eBay connection')
      setConnecting(false)
    }
  }

  const statusColor = getStatusColor()

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => {
          if (!hasCredentials) {
            handleConnect()
          } else {
            setDropdownOpen(!dropdownOpen)
          }
        }}
        disabled={connecting}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          borderRadius: 'var(--theme-radius-sm)',
          background: 'var(--theme-glass-bg)',
          border: '1px solid var(--theme-glass-border)',
          color: 'var(--theme-text-primary)',
          fontSize: '12px',
          fontWeight: 600,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.5px',
          cursor: connecting ? 'wait' : 'pointer',
          transition: 'all 0.2s',
          fontFamily: 'inherit',
          opacity: connecting ? 0.7 : 1,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--theme-glass-border-strong)'
          e.currentTarget.style.boxShadow = 'var(--theme-glow-soft)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--theme-glass-border)'
          e.currentTarget.style.boxShadow = 'none'
        }}
      >
        {/* Status dot */}
        <span style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: statusColor,
          boxShadow: `0 0 6px ${statusColor}`,
          display: 'inline-block',
          flexShrink: 0,
        }} />
        {connecting ? 'Connecting...' : 'eBay'}
        {hasCredentials && <ChevronDown style={{ width: 12, height: 12, opacity: 0.6 }} />}
      </button>

      {/* Dropdown */}
      {dropdownOpen && hasCredentials && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          right: 0,
          minWidth: 260,
          padding: '12px',
          borderRadius: 'var(--theme-radius-md)',
          background: 'var(--theme-glass-bg-heavy)',
          border: '1px solid var(--theme-glass-border-strong)',
          backdropFilter: 'var(--theme-glass-blur)',
          boxShadow: 'var(--theme-glow-medium)',
          zIndex: 100,
        }}>
          {/* Status row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              backgroundColor: statusColor,
              boxShadow: `0 0 8px ${statusColor}`,
              display: 'inline-block',
            }} />
            <span style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--theme-text-primary)',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.5px',
            }}>
              {getStatusLabel()}
            </span>
          </div>

          {/* Details */}
          {refreshStatus?.soonestExpiry && (
            <div style={{
              fontSize: 12,
              color: 'var(--theme-text-secondary)',
              marginBottom: 10,
              lineHeight: 1.4,
            }}>
              Token expires in {refreshStatus.soonestExpiry.hoursUntilExpiry.toFixed(1)}h
            </div>
          )}

          {refreshStatus?.credentials?.[0]?.userId && (
            <div style={{
              fontSize: 11,
              color: 'var(--theme-text-tertiary)',
              marginBottom: 10,
            }}>
              User: {refreshStatus.credentials[0].userId}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => { forceRefresh.mutate(); setDropdownOpen(false) }}
              disabled={forceRefresh.isPending}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '6px 10px',
                borderRadius: 'var(--theme-radius-sm)',
                background: 'var(--theme-glass-bg)',
                border: '1px solid var(--theme-glass-border)',
                color: 'var(--theme-primary)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'inherit',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.5px',
              }}
            >
              <RefreshCw style={{
                width: 12, height: 12,
                animation: forceRefresh.isPending ? 'spin 1s linear infinite' : 'none',
              }} />
              Refresh
            </button>
            <button
              onClick={() => { handleConnect(); setDropdownOpen(false) }}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 4,
                padding: '6px 10px',
                borderRadius: 'var(--theme-radius-sm)',
                background: 'var(--theme-glass-bg)',
                border: '1px solid var(--theme-glass-border)',
                color: 'var(--theme-text-primary)',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                fontFamily: 'inherit',
                textTransform: 'uppercase' as const,
                letterSpacing: '0.5px',
              }}
            >
              <Link2 style={{ width: 12, height: 12 }} />
              Reconnect
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
